/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable, Injector } from '@angular/core';
import {
    concatMap,
    concatMapTo,
    distinctUntilChanged,
    filter,
    first,
    mergeMapTo,
    skip,
    switchMap,
    switchMapTo,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import {
    DataReportCollection,
    FeatureReportCollection,
    LoadFeatureReportsPayload,
    ReportDefinitionCollection,
    ReportTypeValue,
} from '../state/report.model';
import { ClearReports, LaunchVideo, LoadFeatureReports, SetActiveFeatureReports } from '../state/report.actions';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { MapCreated, MapDestroyed } from '../../map/state/active-map.actions';
import { LoggerService } from '../../shared/services/logger.service';
import { MapConfigService } from '../../map/services/mapConfig.service';
import { DashGisOverlay } from '../../../core/ui/overlay/dash-gis-overlay.service';
import { ReportState } from '../state/report.state';
import { Observable } from 'rxjs';
import { OverlayRef } from '@angular/cdk/overlay';
import { SelectionState } from '../../map/state/selection.state';
import { SelectionFeatures } from '../../map/state/selection.model';
import { MapConfigState } from '../../map/state/map-config.state';
import { ReportDisplayOverlayRef } from '../reportDisplay/report-display.overlay-ref';
import { ComponentPortal } from '@angular/cdk/portal';
import { FeatureReportDisplayComponent } from '../reportDisplay/feature-report-display.component';
import { RouterNavigation, RouterState } from '@ngxs/router-plugin';
import { DataReportDisplayComponent } from '../reportDisplay/data-report-display.component';
import { AuthState } from '../../../core/security/state/authState.state';
import { ReportService } from './report.service';
import { RouterStateSnapshot } from '@angular/router';
import { ReportVideoDialogComponent } from '../reportDisplay/report-video-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class ReportDisplayService {
    @Select(ReportState.activeFeatureReports) public activeFeatureReports$: Observable<ReportDefinitionCollection>;
    @Select(ReportState.activeDataReports) public activeDataReports$: Observable<DataReportCollection>;
    @Select(ReportState.featureReports) public featureReports$: Observable<FeatureReportCollection>;
    @Select(ReportState.dataReports) public dataReports$: Observable<DataReportCollection>;

    @Select(RouterState.state) public routerState$: Observable<RouterStateSnapshot>;

    private featureOverlayRef: OverlayRef = null;
    private dataOverlayRef: OverlayRef = null;

    @Select(AuthState.validated) private validated$: Observable<boolean>;
    @Select(SelectionState.selection) private selection$: Observable<SelectionFeatures>;
    @Select(MapConfigState.loaded) private mapConfigLoaded$: Observable<boolean>;

    private readonly dataReportOnMapConfig = {
        width: '700px',
        hasBackdrop: false,
        panelClass: 'dash-gis-floating-panel',
        positionStrategy: this.overlay.position().global().left('240px').top('160px'),
    };
    private readonly featureReportOnMapConfig = {
        width: '300px',
        hasBackdrop: false,
        panelClass: 'dash-gis-floating-panel',
        positionStrategy: this.overlay.position().global().right('45px').top('110px'),
    };
    private readonly reportOnDashboardConfig = {
        maxWidth: '90vw',
        hasBackdrop: false,
        panelClass: 'dash-gis-floating-panel',
        positionStrategy: this.overlay.position().global().centerHorizontally().top('140px'),
    };

    constructor(
        private logger: LoggerService,
        private reportService: ReportService,
        private mapConfig: MapConfigService,
        private overlay: DashGisOverlay,
        private dialog: MatDialog,
        private injector: Injector,
        private actions$: Actions,
        private store: Store
    ) {}

    public init() {
        // load feature report definitions
        this.mapConfigLoaded$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                switchMapTo(this.mapConfig.getAllSelectableLayerNames$()),
                distinctUntilChanged(isEqual),
                switchMap((layerNames: string[]) => {
                    const payload: LoadFeatureReportsPayload = {
                        layerNames,
                        mapName: this.mapConfig.mapName,
                    };
                    return this.store.dispatch(new LoadFeatureReports(payload));
                }),
                take(1)
            )
            .subscribe(
                () => {},
                (err) => this.logger.error('Load feature reports subscription', err)
            );

        this.actions$
            .pipe(
                ofActionSuccessful(MapCreated),
                switchMapTo(this.validated$),
                distinctUntilChanged(),
                filter((b) => b),
                concatMapTo(this.selection$),
                skip(1), // first one is always empty
                concatMap((selection) =>
                    this.store.dispatch(
                        new SetActiveFeatureReports({
                            selection,
                            mapName: this.mapConfig.mapName,
                        })
                    )
                )
                // takeUntil(this.actions$.pipe(ofActionSuccessful(MapDestroyed))),
            )
            .subscribe(
                () => {},
                (err) => this.logger.error(err)
            );

        // show feature reports on selection change or change in current active reports
        this.actions$
            .pipe(
                ofActionSuccessful(SetActiveFeatureReports),
                mergeMapTo(this.activeFeatureReports$),
                distinctUntilChanged(isEqual),
                filter((activeReports) => {
                    let valid = true;
                    const layers = Object.getOwnPropertyNames(activeReports);
                    if (layers.length === 0) {
                        valid = false;
                    } else {
                        for (let i = 0; i < layers.length; i++) {
                            const layer = layers[i];
                            if (activeReports[layer].length === 0) {
                                valid = false;
                                break;
                            }
                        }
                    }
                    return valid;
                }),
                withLatestFrom(this.routerState$)
            )
            .subscribe(
                ([, routerState]) => this.displayReport('feature', routerState),
                (err) => this.logger.error(err)
            );

        this.activeDataReports$
            .pipe(
                filter((dataReports) => dataReports.length > 0),
                withLatestFrom(this.routerState$)
            )
            .subscribe(
                ([, routerState]) => this.displayReport('data', routerState),
                (err) => this.logger.error(err)
            );

        this.selection$
            .pipe(
                filter((selection) => isEqual(selection, {})),
                takeUntil(this.actions$.pipe(ofActionSuccessful(MapDestroyed)))
            )
            .subscribe(
                () => this.closeReportPanels(),
                (err) => this.logger.error(err)
            );

        this.actions$.pipe(ofActionSuccessful(LaunchVideo)).subscribe((action: LaunchVideo) => {
            this.dialog.open(ReportVideoDialogComponent, {
                data: action.payload.url.url,
                autoFocus: false,
            });
        });
    }

    private displayReport(reportType: ReportTypeValue, routerState: RouterStateSnapshot): void {
        const page = routerState.root.firstChild.url[0].path;
        switch (reportType) {
            case 'feature':
                if (this.featureOverlayRef) {
                    if (this.featureOverlayRef.hostElement) {
                        return;
                    } else {
                        this.closeReportPanel('feature');
                    }
                }
                this.displayFeatureReports(page);
                break;

            case 'data':
                if (this.dataOverlayRef) {
                    if (this.dataOverlayRef.hostElement) {
                        return;
                    } else {
                        this.closeReportPanel('data');
                    }
                }
                this.displayDataReports(page);
                break;
        }
    }

    private displayFeatureReports(page: string) {
        const overlayConfig = page === 'dashboard' ? this.reportOnDashboardConfig : this.featureReportOnMapConfig;
        this.featureOverlayRef = this.overlay.create(overlayConfig);
        const reportDisplayOverlayRef = new ReportDisplayOverlayRef(this.featureOverlayRef);
        const injector = this.createInjector(reportDisplayOverlayRef);

        const portal = new ComponentPortal(FeatureReportDisplayComponent, null, injector);
        const panelRef = this.featureOverlayRef.attach<FeatureReportDisplayComponent>(portal);
        panelRef.instance.closePanel.subscribe(() => {
            // panelRef.instance.close();
            // panelRef.destroy();
            this.closeReportPanel('feature');
            this.store.dispatch(new ClearReports({ type: 'feature' }));
        });

        this.featureOverlayRef
            .detachments()
            .pipe(takeUntil(this.actions$.pipe(ofActionSuccessful(MapDestroyed))))
            .subscribe();

        this.actions$
            .pipe(ofActionSuccessful(RouterNavigation), first())
            .subscribe(() => reportDisplayOverlayRef.close());
    }

    private displayDataReports(page: string) {
        const overlayConfig = page === 'dashboard' ? this.reportOnDashboardConfig : this.dataReportOnMapConfig;
        this.dataOverlayRef = this.overlay.create(overlayConfig);
        const reportDisplayOverlayRef = new ReportDisplayOverlayRef(this.dataOverlayRef);
        const injector = this.createInjector(reportDisplayOverlayRef);
        const portal = new ComponentPortal(DataReportDisplayComponent, null, injector);
        const panelRef = this.dataOverlayRef.attach<DataReportDisplayComponent>(portal);
        panelRef.instance.closePanel.pipe(take(1)).subscribe(() => {
            // panelRef.instance.close();
            // panelRef.destroy();
            this.closeReportPanel('data');
            this.store.dispatch(new ClearReports({ type: 'data' }));
        });

        this.actions$
            .pipe(ofActionSuccessful(RouterNavigation), first())
            .subscribe(() => reportDisplayOverlayRef.close());
    }

    private createInjector(reportOverlayRef: ReportDisplayOverlayRef) {
        return Injector.create({
            parent: this.injector,
            providers: [
                {
                    provide: ReportDisplayOverlayRef,
                    useValue: reportOverlayRef,
                },
            ],
        });
    }

    private closeReportPanels() {
        this.closeReportPanel('feature');
        this.closeReportPanel('data');
    }

    private closeReportPanel(type: string) {
        switch (type) {
            case 'feature':
                if (this.featureOverlayRef) {
                    if (this.featureOverlayRef.hostElement) {
                        this.featureOverlayRef.detach();
                    }
                    this.featureOverlayRef.dispose();
                }
                this.featureOverlayRef = null;
                break;
            case 'data':
                if (this.dataOverlayRef) {
                    if (this.dataOverlayRef.hostElement) {
                        this.dataOverlayRef.detach();
                    }
                    this.dataOverlayRef.dispose();
                }
                this.dataOverlayRef = null;
                break;
        }
    }
}
